import React, { useState, useRef } from 'react';
import './NewInspectionForm.css';
import Notification from '../Notification';
import ActionConfirmModal from '../ActionConfirmModal/ActionConfirmModal';
import inspectionService from '../../services/inspectionService';
import { useAuthContext } from '../../context/AuthContext';
import photoService from '../../services/photoService';
import { ROLES, INSURANCE_COMPANIES, VEHICLE_TYPES, MAX_PHOTOS_PER_INSPECTION, OWNER_DOCUMENT_TYPES } from '../../utils/constants';
import { useSpinner } from '../../context/SpinnerContext';
import logger from '../../utils/logger';
import { PROPERTY_CARD_HINTS } from '../../constants/propertyCardHints';
import FieldMismatchNotice from '../FieldMismatchNotice/FieldMismatchNotice';
import { PROPERTY_CARD_FORM_FIELDS, valuesMismatch } from '../../utils/propertyCardCrossCheck';
import config from '../../config/environment';

const { ADMINISTRATOR, CREATOR, CREATOR_SENIOR } = ROLES;

const REQUIRED_INDIVIDUALIZATION_PHOTOS = [
    'Número de chasis',
    'Número de serie',
    'Número de motor'
];

const NewInspectionForm = ({ onClose, onInspectionCreated }) => {
    const { currentUser } = useAuthContext();
    const { showSpinner, hideSpinner } = useSpinner();
    const [notification, setNotification] = useState({ visible: false, message: '', type: 'info' });
    const licensePlateInputRef = useRef(null);

    const showNotification = (message, type = 'info') => {
        setNotification({ visible: true, message, type });
        setTimeout(() => {
            setNotification({ visible: false, message: '', type: 'info' });
        }, 5000);
    };

    // Estados para los datos del formulario
    const [vehicleData, setVehicleData] = useState({
        licensePlate: '',
        vin: '',
        chassis: '',
        engine: '',
        year: '',
        fasecoCode: '',
        type: ''
    });

    const [contactData, setContactData] = useState({
        name: '',
        phone: '',
        email: '',
        docType: '',
        docNumber: ''
    });

    const [insuranceCompany, setInsuranceCompany] = useState(currentUser.insuranceCompany || '');
    const [currentStep, setCurrentStep] = useState(config.ENABLE_PROPERTY_CARD_OCR ? 1 : 2);
    const [uploadedPhotos, setUploadedPhotos] = useState([]);
    const [isAccident, setIsAccident] = useState(false);
    const [accidentType, setAccidentType] = useState(null);
    const [confirmedLicensePlate, setConfirmedLicensePlate] = useState('');
    const [showPlateConfirmModal, setShowPlateConfirmModal] = useState(false);
    const [showPhotosConfirmModal, setShowPhotosConfirmModal] = useState(false);
    const [propertyCardSessionId, setPropertyCardSessionId] = useState('');
    const [propertyCardFiles, setPropertyCardFiles] = useState({
        propertyCardFront: null,
        propertyCardBack: null,
    });
    const [extractedPropertyCard, setExtractedPropertyCard] = useState(null);
    const [skippedPropertyCardRead, setSkippedPropertyCardRead] = useState(false);
    const [propertyCardReadError, setPropertyCardReadError] = useState('');
    const [acceptedMismatches, setAcceptedMismatches] = useState({});
    const [visibleMismatches, setVisibleMismatches] = useState({});

    // Función para manejar cambios en datos del vehículo
    const handleVehicleDataChange = (field, value) => {
        setVehicleData(prev => ({
            ...prev,
            [field]: value
        }));
        if (field === 'licensePlate' && value !== confirmedLicensePlate) {
            setConfirmedLicensePlate('');
        }
        if (acceptedMismatches[field]) {
            setAcceptedMismatches(prev => ({ ...prev, [field]: false }));
        }
    };

    const handleLicensePlateBlur = () => {
        const plate = vehicleData.licensePlate?.trim();
        if (!plate) return;
        if (plate === confirmedLicensePlate) return;
        setShowPlateConfirmModal(true);
    };

    const handleConfirmPlate = () => {
        setConfirmedLicensePlate(vehicleData.licensePlate.trim());
        setShowPlateConfirmModal(false);
    };

    const handleReviewPlate = () => {
        setShowPlateConfirmModal(false);
        setTimeout(() => {
            licensePlateInputRef.current?.focus();
            licensePlateInputRef.current?.select?.();
        }, 0);
    };

    const isPlateConfirmed = () => {
        const plate = vehicleData.licensePlate?.trim();
        return !plate || plate === confirmedLicensePlate;
    };

    // Función para manejar cambios en datos de contacto
    const handleContactDataChange = (field, value) => {
        setContactData(prev => ({
            ...prev,
            [field]: value
        }));
        if (acceptedMismatches[field]) {
            setAcceptedMismatches(prev => ({ ...prev, [field]: false }));
        }
    };

    const getExtractedValue = (formKey) => {
        const mapping = PROPERTY_CARD_FORM_FIELDS.find(item => item.formKey === formKey);
        if (!mapping || !extractedPropertyCard) return '';
        return extractedPropertyCard[mapping.extractedKey] || '';
    };

    const canCrossCheckField = (formKey) => {
        if (!extractedPropertyCard || skippedPropertyCardRead || acceptedMismatches[formKey]) return false;
        return Boolean(getExtractedValue(formKey));
    };

    const handleExtractedFieldBlur = (formKey, currentValue) => {
        if (!canCrossCheckField(formKey)) {
            setVisibleMismatches(prev => ({ ...prev, [formKey]: false }));
            return;
        }
        const extractedValue = getExtractedValue(formKey);
        setVisibleMismatches(prev => ({
            ...prev,
            [formKey]: valuesMismatch(currentValue, extractedValue, formKey),
        }));
    };

    const handlePropertyCardFile = (photoId, file) => {
        if (!file || !file.type.startsWith('image/')) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            setPropertyCardFiles(prev => ({
                ...prev,
                [photoId]: {
                    file,
                    preview: e.target.result,
                    name: file.name,
                },
            }));
            setSkippedPropertyCardRead(false);
            setExtractedPropertyCard(null);
            setPropertyCardReadError('');
        };
        reader.readAsDataURL(file);
    };

    const applyExtractedPropertyCard = (propertyCard) => {
        setExtractedPropertyCard(propertyCard);
        setSkippedPropertyCardRead(false);
        setAcceptedMismatches({});
        setVisibleMismatches({});
        setVehicleData(prev => ({
            ...prev,
            licensePlate: propertyCard.licensePlate || prev.licensePlate,
            vin: propertyCard.vin || prev.vin,
            chassis: propertyCard.chassis || prev.chassis,
            engine: propertyCard.engine || prev.engine,
            year: propertyCard.year || prev.year,
        }));
        setContactData(prev => ({
            ...prev,
            name: propertyCard.ownerName || prev.name,
            docType: propertyCard.docType || prev.docType,
            docNumber: propertyCard.docNumber || prev.docNumber,
        }));
        if (propertyCard.licensePlate) {
            setConfirmedLicensePlate(propertyCard.licensePlate);
        }
    };

    const uploadPropertyCardPhotos = async () => {
        if (propertyCardSessionId) return propertyCardSessionId;
        const { sessionId, uploadUrls } = await inspectionService.generatePropertyCardUploadUrls();
        const frontUrl = uploadUrls.find(item => item.id === 'propertyCardFront');
        const backUrl = uploadUrls.find(item => item.id === 'propertyCardBack');
        await photoService.uploadPhotoToS3(frontUrl, propertyCardFiles.propertyCardFront.file);
        await photoService.uploadPhotoToS3(backUrl, propertyCardFiles.propertyCardBack.file);
        setPropertyCardSessionId(sessionId);
        return sessionId;
    };

    const handleReadPropertyCard = async () => {
        if (!propertyCardFiles.propertyCardFront?.file || !propertyCardFiles.propertyCardBack?.file) {
            showNotification('Carga el frente y el reverso de la tarjeta de propiedad.', 'error');
            return;
        }

        showSpinner('Leyendo el documento...');
        try {
            const sessionId = await uploadPropertyCardPhotos();
            const result = await inspectionService.readPropertyCard(sessionId);
            applyExtractedPropertyCard(result.propertyCard || {});
            setPropertyCardReadError('');
            hideSpinner();
            setCurrentStep(2);
            if (result.missingFields?.length) {
                showNotification(
                    'Se leyeron algunos datos de la tarjeta. Completa los campos que faltan para continuar.',
                    'warning'
                );
            }
        } catch (error) {
            hideSpinner();
            logger.error('Error leyendo la tarjeta de propiedad:', error, null, 'NewInspectionForm');
            setPropertyCardReadError(error.message || 'No se pudo leer el documento.');
        }
    };

    const handleSkipPropertyCardRead = async () => {
        const hasBothPhotos = Boolean(
            propertyCardFiles.propertyCardFront?.file && propertyCardFiles.propertyCardBack?.file
        );

        if (hasBothPhotos) {
            showSpinner('Guardando fotos del documento...');
            try {
                await uploadPropertyCardPhotos();
            } catch (error) {
                logger.error('Error guardando fotos de la tarjeta:', error, null, 'NewInspectionForm');
                showNotification('No se pudieron guardar las fotos del documento. Continúa e ingresa los datos manualmente.', 'warning');
            } finally {
                hideSpinner();
            }
        }

        setSkippedPropertyCardRead(true);
        setExtractedPropertyCard(null);
        setPropertyCardReadError('');
        setAcceptedMismatches({});
        setVisibleMismatches({});
        setCurrentStep(2);
    };

    const isFieldEmpty = (value) => {
        return !value || value.trim() === '';
    };

    // Función para obtener la clase CSS del campo
    const getFieldClassName = (value) => {
        return `form-input ${isFieldEmpty(value) ? 'error' : ''}`;
    };

    const handleMultiplePhotoUpload = (files) => {
        const filesArray = Array.from(files).filter(file => file && file.type.startsWith('image/'));
        const currentCount = uploadedPhotos.length;
        const availableSlots = MAX_PHOTOS_PER_INSPECTION - currentCount;

        if (availableSlots <= 0) {
            showNotification(
                `Ya has alcanzado el límite máximo de ${MAX_PHOTOS_PER_INSPECTION} fotos. Por favor, elimina algunas fotos antes de agregar más.`,
                'error'
            );
            return;
        }

        // Separar las fotos que caben de las que no
        const photosToAdd = filesArray.slice(0, availableSlots);
        const photosRejected = filesArray.slice(availableSlots);

        // Agregar solo las fotos que caben
        photosToAdd.forEach(file => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const newPhoto = {
                    id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    file: file,
                    preview: e.target.result,
                    name: file.name,
                    uploadDate: new Date()
                };
                setUploadedPhotos(prev => [...prev, newPhoto]);
            };
            reader.readAsDataURL(file);
        });

        // Mostrar mensaje informativo si se rechazaron algunas fotos
        if (photosRejected.length > 0) {
            showNotification(
                `Se agregaron ${photosToAdd.length} foto(s). ${photosRejected.length} foto(s) no se pudieron agregar porque exceden el límite máximo de ${MAX_PHOTOS_PER_INSPECTION} fotos por inspección.`,
                'warning'
            );
        }
    };

    // Función para manejar click en área de carga múltiple
    const handleMultiplePhotoClick = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = true;
        input.onchange = (e) => {
            const files = e.target.files;
            if (files) {
                handleMultiplePhotoUpload(files);
            }
        };
        input.click();
    };

    // Función para manejar drag and drop múltiple
    const handleMultipleDrop = (e) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (files) {
            handleMultiplePhotoUpload(files);
        }
    };

    // Función para eliminar foto
    const handleRemovePhoto = (photoId) => {
        setUploadedPhotos(prev => prev.filter(photo => photo.id !== photoId));
    };

    // Función para validar si el paso actual está completo
    const isCurrentStepComplete = () => {
        switch (currentStep) {
            case 1:
                return Boolean(
                    propertyCardFiles.propertyCardFront?.file
                    && propertyCardFiles.propertyCardBack?.file
                    && (extractedPropertyCard || skippedPropertyCardRead)
                );
            case 2:
                const requiredVehicleFields = ['licensePlate', 'vin', 'chassis', 'engine', 'year', 'fasecoCode', 'type'];
                const requiredContactFields = config.ENABLE_OFFICIAL_RECORDS
                    ? ['name', 'phone', 'email']
                    : ['phone', 'email'];
                const accidentFieldsValid = !isAccident || Boolean(accidentType);
                return requiredVehicleFields.every((field) => !isFieldEmpty(vehicleData[field]))
                    && requiredContactFields.every((field) => !isFieldEmpty(contactData[field]))
                    && !isFieldEmpty(insuranceCompany)
                    && accidentFieldsValid;
            case 3:
                return uploadedPhotos.length > 0;
            default:
                return false;
        }
    };

    // Función para ir al siguiente paso
    const nextStep = () => {
        if (!isPlateConfirmed() && vehicleData.licensePlate?.trim()) {
            setShowPlateConfirmModal(true);
            return;
        }
        if (isCurrentStepComplete()) {
            setCurrentStep(prev => prev + 1);
        }
    };

    const handleCreateInspectionClick = () => {
        if (!isCurrentStepComplete()) return;
        setShowPhotosConfirmModal(true);
    };

    const handleConfirmPhotosAndCreate = () => {
        setShowPhotosConfirmModal(false);
        createInspection();
    };

    // Función para ir al paso anterior
    const prevStep = () => {
        setCurrentStep(prev => Math.max(config.ENABLE_PROPERTY_CARD_OCR ? 1 : 2, prev - 1));
    };

    const fillInspectionData = () => {
        setVehicleData({
            licensePlate: 'ABC123',
            vin: '1HGCM82633A004352',
            chassis: 'CHS1234567890',
            engine: 'ENG987654321',
            year: '2020',
            fasecoCode: 'FSC001',
            type: 'LIVIANO'
        });
        setConfirmedLicensePlate('ABC123');
        setContactData({
            name: 'Juan Pérez',
            phone: '5551234567',
            email: 'juan.perez@example.com',
            docType: 'CC',
            docNumber: '1010111935'
        });
        setInsuranceCompany('HOLDING');
        setIsAccident(false);
        setAccidentType(null);
        setSkippedPropertyCardRead(true);
        setCurrentStep(2);
    };

    const enableFillInspectionData = () => {
        return currentUser.role === ADMINISTRATOR && window.location.origin.includes('localhost');
    };

    const isCompanyAllowed = (companyValue) => {
        if (currentUser.role === ADMINISTRATOR || currentUser.role === ROLES.INSPECTOR) {
            return true;
        }
        
        if (currentUser.role === CREATOR || currentUser.role === CREATOR_SENIOR) {
            const userCompanies = currentUser.insuranceCompany?.split(',') || [];
            return userCompanies.some(userCompany => 
                userCompany.trim().toLowerCase() === companyValue.toLowerCase()
            );
        }
        
        return false;
    };

    const getAvailableCompanies = () => {
        return INSURANCE_COMPANIES.filter(company => {
            if (company.label === 'TODAS') return false;
            return isCompanyAllowed(company.value);
        });
    };

    // Función para crear la inspección
    const createInspection = async () => {
        logger.info('Creando inspección...', {
            currentUserEmail: currentUser?.email,
        }, 'NewInspectionForm');
        showSpinner('Creando inspección...');
        try {

            const inspectionData = {
                userId: currentUser.id,
                userName: currentUser.name,
                userEmail: currentUser.email,
                inspectionId: 'DIC',
                licensePlate: vehicleData.licensePlate,
                type: vehicleData.type || null,
                vin: vehicleData.vin || null,
                chassis: vehicleData.chassis || null,
                name: contactData.name,
                phone: contactData.phone,
                email: contactData.email,
                docType: contactData.docType,
                docNumber: contactData.docNumber,
                insuranceCompany: insuranceCompany || null,
                engine: vehicleData.engine || null,
                year: vehicleData.year || null,
                fasecoCode: vehicleData.fasecoCode || null,
                notes: null,
                isAccident: isAccident,
                accidentType: isAccident ? accidentType : null,
                propertyCardSessionId: propertyCardSessionId || null
            };

            const result = await inspectionService.createInspection(inspectionData);

            const inspectionId = result.inspectionId;

            // 2. Subir fotos si hay
            if (uploadedPhotos.length > 0) {
                try {
                    await photoService.uploadMultiplePhotos(
                        uploadedPhotos.map(photo => photo.file),
                        inspectionId,
                        (photoIndex, progress) => {
                            console.log(`Foto ${photoIndex + 1}: ${progress.toFixed(1)}%`);
                        }
                    );

                    logger.info('Fotos subidas exitosamente...', {
                        inspectionId: inspectionId,
                        photosCount: uploadedPhotos.length,
                    }, 'NewInspectionForm');
                } catch (uploadError) {
                    logger.error('Error subiendo fotos:', uploadError, null, 'NewInspectionForm');
                }
            }

            hideSpinner();

            showNotification('Inspección creada exitosamente', 'success');
            logger.info('Inspección creada exitosamente...', {
                inspectionId: inspectionId,
                currentUserEmail: currentUser?.email,
            }, 'NewInspectionForm');

            if (onInspectionCreated && typeof onInspectionCreated === 'function') {
                onInspectionCreated(result.inspection || result);
            }

            if (typeof onClose === 'function') {
                onClose();
            }

        } catch (error) {
            logger.error('Error creating inspection:', error, null, 'NewInspectionForm');
            hideSpinner();
            showNotification('Error al crear la inspección. Inténtalo de nuevo.', 'error');
        }
    };

    const renderMismatchNotice = (formKey) => {
        if (!canCrossCheckField(formKey) || !visibleMismatches[formKey]) return null;
        return (
            <FieldMismatchNotice
                extractedValue={getExtractedValue(formKey)}
                onAcceptTyped={() => {
                    setAcceptedMismatches(prev => ({ ...prev, [formKey]: true }));
                    setVisibleMismatches(prev => ({ ...prev, [formKey]: false }));
                }}
                onDismiss={() => setVisibleMismatches(prev => ({ ...prev, [formKey]: false }))}
            />
        );
    };

    const renderPropertyCardSlot = (photoId) => {
        const hint = PROPERTY_CARD_HINTS[photoId];
        const selected = propertyCardFiles[photoId];
        return (
            <div className="property-card-slot" key={photoId}>
                <div className="property-card-slot-body">
                    <h3>{hint.title}</h3>
                    <p className="property-card-subtitle">{hint.subtitle}</p>
                    <div className="property-card-image-frame">
                        <img
                            src={selected?.preview || hint.image}
                            alt={selected?.name || hint.title}
                        />
                    </div>
                    <p className="property-card-hint-text">{hint.hint}</p>
                </div>
                <div className="property-card-slot-footer">
                    {selected?.preview ? (
                        <button
                            type="button"
                            className="property-card-upload property-card-remove"
                            onClick={() => {
                                setPropertyCardFiles(prev => ({ ...prev, [photoId]: null }));
                                setExtractedPropertyCard(null);
                                setSkippedPropertyCardRead(false);
                            }}
                        >
                            Quitar
                        </button>
                    ) : (
                        <label className="property-card-upload">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handlePropertyCardFile(photoId, e.target.files?.[0])}
                            />
                            Cargar foto
                        </label>
                    )}
                </div>
            </div>
        );
    };

    // Renderizar paso actual
    const renderCurrentStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="form-step">
                        <h2>Tarjeta de propiedad</h2>
                        <p className="step-description">
                            Carga el frente y el reverso de la licencia de tránsito para leer los datos del vehículo y del propietario.
                            Si vas a ingresar todo a mano, puedes omitir la lectura y continuar.
                        </p>
                        <div className="property-card-slots">
                            {renderPropertyCardSlot('propertyCardFront')}
                            {renderPropertyCardSlot('propertyCardBack')}
                        </div>
                        {propertyCardReadError && (
                            <div className="property-card-read-error" role="alert">
                                <p>{propertyCardReadError}</p>
                                <div className="property-card-read-error-actions">
                                    <button
                                        type="button"
                                        className="new-inspection-form-btn new-inspection-form-btn-secondary"
                                        onClick={() => {
                                            setPropertyCardFiles({ propertyCardFront: null, propertyCardBack: null });
                                            setPropertyCardReadError('');
                                        }}
                                    >
                                        Volver a cargar
                                    </button>
                                    <button
                                        type="button"
                                        className="new-inspection-form-btn new-inspection-form-btn-primary"
                                        onClick={handleSkipPropertyCardRead}
                                    >
                                        Omitir OCR y continuar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            case 2:
                return (
                    <div className="form-step">
                        <h2>Datos de la Inspección</h2>

                        <div className="form-sections">
                            {/* Datos del Vehículo */}
                            <div className="form-section">
                                <h3>Información de Siniestro</h3>
                                <div className="form-group">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={isAccident}
                                            onChange={(e) => {
                                                setIsAccident(e.target.checked);
                                                if (!e.target.checked) {
                                                    setAccidentType(null);
                                                }
                                            }}
                                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                        />
                                        <span>¿Es un siniestro?</span>
                                    </label>
                                </div>
                                {isAccident && (
                                    <div className="form-group">
                                        <label htmlFor="accidentType">Tipo de Siniestro</label>
                                        <select
                                            id="accidentType"
                                            value={accidentType || ''}
                                            className={getFieldClassName(accidentType)}
                                            onChange={(e) => setAccidentType(e.target.value)}
                                            required
                                        >
                                            <option value="" selected>Selecciona una opción</option>
                                            <option value="own">Siniestro Propio</option>
                                            <option value="third_party">Siniestro de Tercero</option>
                                        </select>
                                    </div>
                                )}

                                <h3>Datos del Vehículo</h3>
                                <div className="form-group">
                                    <label htmlFor="placa">Placa del Vehículo</label>
                                    <input
                                        type="text"
                                        id="placa"
                                        ref={licensePlateInputRef}
                                        className={getFieldClassName(vehicleData.licensePlate)}
                                        value={vehicleData.licensePlate}
                                        onChange={(e) => handleVehicleDataChange('licensePlate', e.target.value.toUpperCase())}
                                        onBlur={(e) => {
                                            handleLicensePlateBlur();
                                            handleExtractedFieldBlur('licensePlate', e.target.value);
                                        }}
                                        placeholder="ABC123"
                                        required
                                    />
                                    {renderMismatchNotice('licensePlate')}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="vin">Número VIN</label>
                                    <input
                                        type="text"
                                        id="vin"
                                        className={getFieldClassName(vehicleData.vin)}
                                        value={vehicleData.vin}
                                        onChange={(e) => handleVehicleDataChange('vin', e.target.value)}
                                        onBlur={(e) => handleExtractedFieldBlur('vin', e.target.value)}
                                        placeholder="1HGBH41JXMN109186"
                                        required
                                    />
                                    {renderMismatchNotice('vin')}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="chassis">Número de Chasis</label>
                                    <input
                                        type="text"
                                        id="chassis"
                                        className={getFieldClassName(vehicleData.chassis)}
                                        value={vehicleData.chassis}
                                        onChange={(e) => handleVehicleDataChange('chassis', e.target.value)}
                                        onBlur={(e) => handleExtractedFieldBlur('chassis', e.target.value)}
                                        placeholder="ABC123456789"
                                        required
                                    />
                                    {renderMismatchNotice('chassis')}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="engine">Número de Motor</label>
                                    <input
                                        type="text"
                                        id="engine"
                                        className={getFieldClassName(vehicleData.engine)}
                                        value={vehicleData.engine}
                                        onChange={(e) => handleVehicleDataChange('engine', e.target.value)}
                                        onBlur={(e) => handleExtractedFieldBlur('engine', e.target.value)}
                                        placeholder="ABC123456789"
                                        required
                                    />
                                    {renderMismatchNotice('engine')}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="year">Modelo</label>
                                    <input
                                        type="number"
                                        id="year"
                                        className={getFieldClassName(vehicleData.year)}
                                        value={vehicleData.year}
                                        onChange={(e) => handleVehicleDataChange('year', e.target.value)}
                                        onBlur={(e) => handleExtractedFieldBlur('year', e.target.value)}
                                        placeholder="2020"
                                        required
                                    />
                                    {renderMismatchNotice('year')}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="fasecoCode">Código Faseco</label>
                                    <input
                                        type="text"
                                        id="fasecoCode"
                                        className={getFieldClassName(vehicleData.fasecoCode)}
                                        value={vehicleData.fasecoCode}
                                        onChange={(e) => handleVehicleDataChange('fasecoCode', e.target.value)}
                                        placeholder="ABC123456789"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="type">Tipo de Vehículo</label>
                                    <select
                                        id="type"
                                        value={vehicleData.type}
                                        className={getFieldClassName(vehicleData.type)}
                                        onChange={(e) => handleVehicleDataChange('type', e.target.value)}
                                        required
                                    >
                                        <option value="" selected>Selecciona una opción</option>
                                        {VEHICLE_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Datos de Contacto y Aliado */}
                            <div className="form-section">
                                <h3>Datos de Contacto</h3>
                                {config.ENABLE_OFFICIAL_RECORDS && (
                                <>
                                <div className="form-group">
                                    <label htmlFor="nombre">Nombre del Propietario</label>
                                    <input
                                        type="text"
                                        id="nombre"
                                        className={getFieldClassName(contactData.name)}
                                        value={contactData.name}
                                        onChange={(e) => handleContactDataChange('name', e.target.value)}
                                        onBlur={(e) => handleExtractedFieldBlur('name', e.target.value)}
                                        placeholder="Juan Pérez"
                                        required
                                    />
                                    {renderMismatchNotice('name')}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="docType">Tipo de documento</label>
                                    <select
                                        id="docType"
                                        className="form-input"
                                        value={contactData.docType}
                                        onChange={(e) => handleContactDataChange('docType', e.target.value)}
                                        onBlur={(e) => handleExtractedFieldBlur('docType', e.target.value)}
                                    >
                                        <option value="">Selecciona una opción</option>
                                        {OWNER_DOCUMENT_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                    {renderMismatchNotice('docType')}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="docNumber">Número de documento</label>
                                    <input
                                        type="text"
                                        id="docNumber"
                                        className="form-input"
                                        value={contactData.docNumber}
                                        onChange={(e) => handleContactDataChange('docNumber', e.target.value)}
                                        onBlur={(e) => handleExtractedFieldBlur('docNumber', e.target.value)}
                                        placeholder="1234567890"
                                    />
                                    {renderMismatchNotice('docNumber')}
                                </div>
                                </>
                                )}
                                <div className="form-group">
                                    <label htmlFor="celular">Celular de Contacto</label>
                                    <input
                                        type="number"
                                        id="celular"
                                        className={getFieldClassName(contactData.phone)}
                                        value={contactData.phone}
                                        onChange={(e) => handleContactDataChange('phone', e.target.value)}
                                        placeholder="300 123 4567"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="email">Correo Electrónico</label>
                                    <input
                                        type="email"
                                        id="email"
                                        className={getFieldClassName(contactData.email)}
                                        value={contactData.email}
                                        onChange={(e) => handleContactDataChange('email', e.target.value)}
                                        placeholder="juan.perez@email.com"
                                        required
                                    />
                                </div>

                                <h3 style={{ marginTop: '30px' }}>Datos del Aliado</h3>
                                <div className="form-group">
                                    <label htmlFor="insurance">Nombre de la Compañía</label>
                                    <select
                                        id="insuranceCompany"
                                        value={insuranceCompany}
                                        className={getFieldClassName(insuranceCompany)}
                                        onChange={(e) => setInsuranceCompany(e.target.value)}
                                        required
                                    >
                                        <option value="" selected>Selecciona una compañía</option>
                                        {getAvailableCompanies().map(company => (
                                            <option key={company.value} value={company.value}>
                                                {company.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="form-step">
                        <h2>Carga de Fotos</h2>
                        <p className="step-description">
                            Carga las fotos requeridas para la inspección.
                        </p>

                        <div className="required-photos-checklist" role="note">
                            <div className="required-photos-header">
                                <span className="required-photos-icon" aria-hidden="true">!</span>
                                <h3 className="required-photos-title">Imágenes para individualizar el vehículo</h3>
                            </div>
                            <p className="required-photos-intro">
                                Asegúrate de incluir estas fotos entre las que subas:
                            </p>
                            <ul className="required-photos-list">
                                {REQUIRED_INDIVIDUALIZATION_PHOTOS.map((item) => (
                                    <li key={item}>
                                        <span className="required-photos-bullet" aria-hidden="true">•</span>
                                        <span className="required-photos-label">{item}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="required-photos-note">
                                Puedes agregar el resto de evidencias junto con estas imágenes.
                            </p>
                        </div>

                        {/* Área de carga múltiple */}
                        <div className={`multiple-upload-area ${uploadedPhotos.length >= MAX_PHOTOS_PER_INSPECTION ? 'disabled' : ''}`}>
                            <div
                                className="upload-zone"
                                onClick={uploadedPhotos.length >= MAX_PHOTOS_PER_INSPECTION ? undefined : handleMultiplePhotoClick}
                                onDrop={uploadedPhotos.length >= MAX_PHOTOS_PER_INSPECTION ? undefined : handleMultipleDrop}
                                onDragOver={(e) => e.preventDefault()}
                                onDragEnter={(e) => e.preventDefault()}
                            >
                                <div className="upload-content">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="7,10 12,15 17,10"></polyline>
                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                    </svg>
                                    <h3>Haz clic o arrastra las fotos aquí</h3>
                                    <p>Puedes seleccionar múltiples fotos de una vez</p>
                                    <p className="photo-limit-info">
                                        Máximo {MAX_PHOTOS_PER_INSPECTION} fotos por inspección
                                        {uploadedPhotos.length > 0 && (
                                            <span className="photo-count">
                                                ({uploadedPhotos.length}/{MAX_PHOTOS_PER_INSPECTION} fotos seleccionadas)
                                                {uploadedPhotos.length < MAX_PHOTOS_PER_INSPECTION && (
                                                    <span className="photo-remaining">
                                                        {' '}• Puedes agregar {MAX_PHOTOS_PER_INSPECTION - uploadedPhotos.length} foto(s) más
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Grid de fotos subidas */}
                        {uploadedPhotos.length > 0 && (
                            <div className="uploaded-photos-section">
                                <h3>Fotos Subidas ({uploadedPhotos.length})</h3>
                                <div className="photos-grid">
                                    {uploadedPhotos.map(photo => (
                                        <div key={photo.id} className="photo-item">
                                            <div className="photo-preview-container">
                                                <img src={photo.preview} alt={photo.name} className="photo-preview" />
                                                <div className="photo-overlay">
                                                    <button
                                                        className="remove-photo-btn"
                                                        onClick={() => handleRemovePhoto(photo.id)}
                                                        title="Eliminar foto"
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M18 6L6 18"></path>
                                                            <path d="M6 6l12 12"></path>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="photo-info">
                                                <span className="photo-name">{photo.name}</span>
                                                <span className="photo-date">
                                                    {photo.uploadDate.toLocaleTimeString()}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="new-inspection-form">
            <div className="form-container">
                {/* Progress Bar */}
                <div className="progress-bar">
                    {config.ENABLE_PROPERTY_CARD_OCR && (
                    <div className="progress-step">
                        <div className={`step-number ${currentStep >= 1 ? 'active' : ''}`}>1</div>
                        <span className="step-label">Tarjeta de propiedad</span>
                    </div>
                    )}
                    <div className="progress-step">
                        <div className={`step-number ${currentStep >= 2 ? 'active' : ''}`}>{config.ENABLE_PROPERTY_CARD_OCR ? 2 : 1}</div>
                        <span className="step-label">Datos de la Inspección</span>
                    </div>
                    <div className="progress-step">
                        <div className={`step-number ${currentStep >= 3 ? 'active' : ''}`}>{config.ENABLE_PROPERTY_CARD_OCR ? 3 : 2}</div>
                        <span className="step-label">Carga de Fotos</span>
                    </div>
                </div>

                {/* Form Content */}
                <div className="form-content">
                    {renderCurrentStep()}
                </div>

                {/* Navigation Buttons */}
                <div className="form-navigation">
                    {currentStep > (config.ENABLE_PROPERTY_CARD_OCR ? 1 : 2) && (
                        <button
                            type="button"
                            className="new-inspection-form-btn new-inspection-form-btn-secondary"
                            onClick={prevStep}
                        >
                            Anterior
                        </button>
                    )}

                    {currentStep === 1 ? (
                        <>
                            <button
                                type="button"
                                className="new-inspection-form-btn new-inspection-form-btn-secondary"
                                onClick={handleSkipPropertyCardRead}
                            >
                                Omitir OCR y continuar
                            </button>
                            <button
                                type="button"
                                className={`new-inspection-form-btn new-inspection-form-btn-primary ${!propertyCardFiles.propertyCardFront?.file || !propertyCardFiles.propertyCardBack?.file ? 'disabled' : ''}`}
                                onClick={handleReadPropertyCard}
                                disabled={!propertyCardFiles.propertyCardFront?.file || !propertyCardFiles.propertyCardBack?.file}
                            >
                                Leer documento
                            </button>
                            {enableFillInspectionData() && <button
                                type="button"
                                className={`new-inspection-form-btn new-inspection-form-btn-primary`}
                                onClick={fillInspectionData}
                            >
                                Llenar datos de la inspección
                            </button>}
                        </>
                    ) : currentStep < 3 ? (
                        <><button
                            type="button"
                            className={`new-inspection-form-btn new-inspection-form-btn-primary ${!isCurrentStepComplete() ? 'disabled' : ''}`}
                            onClick={nextStep}
                            disabled={!isCurrentStepComplete()}
                        >
                            Siguiente
                        </button>
                            {enableFillInspectionData() && <button
                                type="button"
                                className={`new-inspection-form-btn new-inspection-form-btn-primary`}
                                onClick={fillInspectionData}
                            >
                                Llenar datos de la inspección
                            </button>}</>
                    ) : (
                        <button
                            type="button"
                            className={`new-inspection-form-btn new-inspection-form-btn-primary ${!isCurrentStepComplete() ? 'disabled' : ''}`}
                            onClick={handleCreateInspectionClick}
                            disabled={!isCurrentStepComplete()}
                        >
                            Crear Inspección
                        </button>
                    )}
                </div>
            </div>
            <ActionConfirmModal
                isOpen={showPlateConfirmModal}
                onClose={handleReviewPlate}
                onConfirm={handleConfirmPlate}
                title="Confirmar placa"
                confirmText="Continuar"
                cancelText="Revisar placa"
                message={
                    <p className="action-confirm-message">
                        Vas a registrar la placa{' '}
                        <span className="action-confirm-plate">{vehicleData.licensePlate}</span>.
                        {' '}¿Corresponde al vehículo en inspección?
                    </p>
                }
            />
            <ActionConfirmModal
                isOpen={showPhotosConfirmModal}
                onClose={() => setShowPhotosConfirmModal(false)}
                onConfirm={handleConfirmPhotosAndCreate}
                title="¿Fotos listas para enviar?"
                confirmText="Sí, crear inspección"
                cancelText="Revisar fotos"
                message={
                    <p className="action-confirm-message">
                        ¿Confirmas que adjuntaste las fotos de{' '}
                        <span className="action-confirm-highlight">chasis</span>,{' '}
                        <span className="action-confirm-highlight">número de serie</span>
                        {' '}y{' '}
                        <span className="action-confirm-highlight">número de motor</span>?
                    </p>
                }
            />
            {/* Notification Component */}
            <Notification
                visible={notification.visible}
                message={notification.message}
                type={notification.type}
                onClose={() => setNotification({ visible: false, message: '', type: 'info' })}
            />
        </div>
    );
};

export default NewInspectionForm; 